import { AlertCircle, ArrowLeft, Home, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/logo";
import * as Sentry from "@sentry/react";

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback = ({ error, resetErrorBoundary }: ErrorFallbackProps) => {
  Sentry.captureException(error);

  return (
    // `theme-user` lives here rather than on the dashboard layout root: the
    // ErrorBoundary *wraps* that root, so when this fallback replaces its
    // children the scope is gone and every dark-* class below would resolve
    // to its light @theme value. See docs/WAVE-1.md.
    <div className="theme-user bg-dark-background-main flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <Logo className="text-dark-text-50 mb-8 h-7 w-auto opacity-90" />

      <div className="border-dark-stroke-3 bg-dark-background-secondary w-full max-w-md rounded-2xl border px-8 py-10 shadow-sm">
        <div className="flex flex-col items-center text-center">
          <div className="bg-dark-error-300/10 mb-5 flex size-16 items-center justify-center rounded-full">
            <AlertCircle className="text-dark-error-300 size-8" />
          </div>

          <h1 className="text-dark-text-50 mb-2 text-2xl font-semibold">
            Something went wrong
          </h1>
          <p className="text-dark-text-100 mb-1 text-sm leading-5">
            We&apos;re sorry for the hiccup. Our team has been notified and
            we&apos;re working to fix it.
          </p>
          <p className="text-dark-text-300 mb-6 text-xs">
            Try refreshing the page or returning to the dashboard.
          </p>

          {import.meta.env.DEV && (
            <details className="bg-dark-card-3 mb-6 w-full rounded-xl p-3 text-left text-xs">
              <summary className="text-dark-text-100 mb-2 cursor-pointer font-medium">
                Error Details (Dev Only)
              </summary>
              <pre className="text-dark-text-300 overflow-auto font-mono text-[10px] leading-4 whitespace-pre-wrap">
                {error.message}
                {"\n\n"}
                {error.stack}
              </pre>
            </details>
          )}

          <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              onClick={resetErrorBoundary}
              variant="outline"
              className="border-dark-stroke-3 text-dark-text-100 hover:bg-dark-card-3 hover:text-dark-text-50 gap-2"
            >
              <RefreshCw className="size-4" />
              Try Again
            </Button>
            <Button
              onClick={() => {
                window.location.href = "/dashboard";
              }}
              className="gradient-button gap-2"
            >
              <Home className="size-4" />
              Go to Dashboard
            </Button>
          </div>

          <button
            onClick={() => window.history.back()}
            className="text-dark-text-300 hover:text-dark-primary-main mt-4 flex items-center gap-1 text-xs transition-colors"
          >
            <ArrowLeft className="size-3" />
            Go back
          </button>
        </div>
      </div>

      <p className="text-dark-text-300 mt-6 text-xs">
        If this issue keeps happening, please contact support.
      </p>
    </div>
  );
};

export default ErrorFallback;
